import {fetchMe} from '@/utils/loginHelper'
import {
  Box,
  Link as ChakraLink,
  Flex,
  GridItem,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useTheme,
} from '@chakra-ui/react'
import isEmpty from 'lodash/isEmpty'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import {useRouter} from 'next/router'
import {SUPPORTED_LANGUAGES} from '../../constants/languages'
import {AVAILABLE_PLANS, BASIC_PLAN, FREE_PLAN, PRO_PLAN, REGULAR_PLAN} from '../../constants/plans'
import {CEDILLE_CONTACT, getCedilleRedirectURL} from '../../constants/routes'
import Plan from '../../dataModels/Plan'
import {useAuth} from '../../hooks/useAuth'
import {useIsMobile} from '../../hooks/useIsMobile'
import emoji_information from '../../public/assets/emoji_information.png'
import emoji_ruler from '../../public/assets/emoji_ruler.png'
import {goToCheckout, goToPortal} from '../../utils/stripeHelper'
import LoginModal from '../Modals/LoginModal'
import CustomPriceBlock from '../Modules/CustomPriceBlock'
import PriceBlock from '../Modules/PriceBlock'
import SubscriptionManagement from '../Modules/SubscriptionManagement'

interface Props {
  currentPlan: Plan
}

const UserSubscription = ({currentPlan}: Props) => {
  const theme = useTheme()
  const router = useRouter()
  const {t} = useTranslation('subscription')

  const isMobile = useIsMobile()

  const {state, setState} = useAuth()
  const connectionNeeded = useDisclosure()

  const subscribeButtonPressed = async (plan: Plan) => {
    if (!state.user) {
      connectionNeeded.onOpen()
      return
    }

    const publicUser = await fetchMe(router.locale as SUPPORTED_LANGUAGES)

    if (!publicUser || isEmpty(publicUser.stripeCustomerId)) return
    setState({...state, publicUser})
    isEmpty(publicUser.stripeSubscriptionId)
      ? await goToCheckout(router, plan)
      : await goToPortal(
          publicUser.stripeCustomerId!,
          publicUser.stripeSubscriptionId!,
          router,
          plan
        )
  }

  return (
    <VStack spacing={7}>
      {connectionNeeded.isOpen && (
        <LoginModal isOpen={connectionNeeded.isOpen} onClose={connectionNeeded.onClose} />
      )}
      <VStack
        w='full'
        p={7}
        spacing={5}
        borderRadius={theme.radii.big}
        background={theme.colors.layers.appLevel1}
        align='center'>
        {!AVAILABLE_PLANS.includes(currentPlan) && (
          <Stack
            direction={isMobile ? 'column' : 'row'}
            p={6}
            borderRadius={theme.radii.small}
            backgroundColor={theme.colors.layers.appLevel2}>
            <Flex mr={2} placeItems='center' justifyContent='center'>
              <Box>
                <Image width={42} height={42} src={emoji_information} alt='emoji_warning' />
              </Box>
            </Flex>
            <Text color={theme.colors.text.basic}>{t('subscription_information_custom_plan')}</Text>
          </Stack>
        )}
        <SimpleGrid
          w='full'
          columns={[1, 1, 1, 2, 4, 4]}
          spacing={5}
          borderRadius={theme.radii.big}>
          <GridItem colSpan={1}>
            <PriceBlock
              pt={[0, 0, 0, 0, 7, 7]}
              plan={t('subscription_plan_free')}
              deactivated={currentPlan === FREE_PLAN}
              free
              price={FREE_PLAN.price}
              onClick={async () => {
                await subscribeButtonPressed(FREE_PLAN)
              }}
              customIcon={<Image width={42} height={42} src={FREE_PLAN.icon} alt='emoji_eyes' />}
              points={[
                {
                  text: t('subscription_nb_per_month_free', {
                    first14daysNbGenerations: FREE_PLAN.nbOfGenerations14firstDays,
                    nbGenerations: FREE_PLAN.nbOfGenerations,
                  }),
                },
                {text: t('subscription_all_skills')},
              ]}
            />
          </GridItem>
          <GridItem colSpan={1}>
            <PriceBlock
              pt={[0, 0, 0, 0, 7, 7]}
              plan={t('subscription_plan_basic')}
              deactivated={currentPlan === BASIC_PLAN}
              price={BASIC_PLAN.price}
              onClick={async () => {
                await subscribeButtonPressed(BASIC_PLAN)
              }}
              customIcon={
                <Image width={42} height={42} src={BASIC_PLAN.icon} alt='emoji_telescope' />
              }
              points={[
                {
                  text: t('subscription_nb_per_month', {nbGenerations: BASIC_PLAN.nbOfGenerations}),
                },
                {text: t('subscription_all_skills')},
              ]}
            />
          </GridItem>
          <GridItem colSpan={1}>
            <PriceBlock
              pt={[0, 0, 0, 0, 0, 0]}
              plan={t('subscription_plan_regular')}
              price={REGULAR_PLAN.price}
              highlighted
              deactivated={currentPlan === REGULAR_PLAN}
              customIcon={
                <Image width={42} height={42} src={REGULAR_PLAN.icon} alt='emoji_ringed_planet' />
              }
              onClick={async () => {
                await subscribeButtonPressed(REGULAR_PLAN)
              }}
              points={[
                {
                  text: t('subscription_nb_per_month', {
                    nbGenerations: REGULAR_PLAN.nbOfGenerations,
                  }),
                },
                {text: t('subscription_all_skills')},
              ]}
            />
          </GridItem>
          <GridItem colSpan={1}>
            <PriceBlock
              pt={[0, 0, 0, 7, 7, 7]}
              plan={t('subscription_plan_pro')}
              price={PRO_PLAN.price}
              deactivated={currentPlan === PRO_PLAN}
              customIcon={<Image width={42} height={42} src={PRO_PLAN.icon} alt='emoji_globe' />}
              onClick={async () => {
                await subscribeButtonPressed(PRO_PLAN)
              }}
              points={[
                {text: t('subscription_nb_per_month', {nbGenerations: PRO_PLAN.nbOfGenerations})},
                {text: t('subscription_all_skills')},
              ]}
            />
          </GridItem>
        </SimpleGrid>
        <CustomPriceBlock
          points={[
            {text: t('subscription_custom_number_of_generations')},
            {text: t('subscription_custom_skills')},
            {text: t('subscription_custom_api_integration')},
            {text: t('subscription_technical_support')},
          ]}
          customIcon={<Image width={42} height={42} src={emoji_ruler} alt='emoji_ruler' />}
        />
        <HStack m={0} p={0} w='full'>
          <Text color={theme.colors.text.basic} textAlign={['center', null, 'start']}>
            <Trans
              i18nKey='subscription:subscription_ask_questions_encouragement_text'
              components={[
                <ChakraLink
                  key='0'
                  _hover={{opacity: 0.7}}
                  fontWeight={theme.fontWeights.normal}
                  variant='textLightPrimary'
                  href={getCedilleRedirectURL(CEDILLE_CONTACT, router.locale)}
                  isExternal
                />,
              ]}
              values={{contact: t('subscription_ask_questions_encouragement_link')}}
            />
          </Text>
        </HStack>
      </VStack>
      {state.publicUser &&
        state.publicUser.stripeSubscriptionId &&
        state.publicUser.stripeCustomerId && <SubscriptionManagement />}
    </VStack>
  )
}
export default UserSubscription
